import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	ElementRef,
	EventEmitter,
	input,
	Input,
	OnChanges,
	Output,
	signal,
	SimpleChanges,
	ViewChild,
	inject,
} from '@angular/core';
import { isEmpty } from 'lodash-es';
import { Option } from '../utils/option';
import { MultiSelectCheckboxComponent } from '../multi-select-checkbox/multi-select-checkbox.component';
import { NgClickOutsideDelayOutsideDirective, NgClickOutsideExcludeDirective } from 'ng-click-outside2';
import { includesIgnoreCaseAndDiacritics } from '@cumlaude/shared-utils';
import { IconComponent } from '@cumlaude/shared-components-icons';

@Component({
	selector: 'app-multi-select-dropdown',
	templateUrl: './multi-select-dropdown.component.html',
	styleUrls: ['./multi-select-dropdown.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [NgClickOutsideExcludeDirective, MultiSelectCheckboxComponent, NgClickOutsideDelayOutsideDirective, IconComponent],
})
export class MultiSelectDropdownComponent<T> implements OnChanges {
	private readonly changeDetector = inject(ChangeDetectorRef);

	@ViewChild('dropdown', { read: ElementRef }) dropdownRef!: ElementRef;

	@Input()
	placeholder?: string;

	@Input()
	allOptions: Option<T>[] = [];

	@Input()
	value: T[] = [];

	@Input()
	showSearchInput: boolean = false;

	@Input()
	showSearchInputInside: boolean = true;

	searchInput?: string;

	@Input()
	matchesSearch: (option: Option<T>, searchInput?: string) => boolean = (option, searchInput) =>
		!searchInput || includesIgnoreCaseAndDiacritics(option.text, searchInput);

	@Input()
	summaryAll?: string;

	label = input<string>();

	@Output()
	valueChange = new EventEmitter<T[]>();

	enabled = input(true);

	public showDropdown = signal(false);
	isValueBoxHovered = signal(false);

	protected readonly chevronIconName = computed(() => (this.showDropdown() ? 'chevron-up' : 'chevron-down'));

	protected readonly chevronIconColor = computed(() => {
		if (!this.enabled()) return 'background-2';
		return this.showDropdown() || this.isValueBoxHovered() ? 'primary-1' : 'typography-5';
	});

	ngOnChanges(changes: SimpleChanges) {
		if ('allOptions' in changes) this.searchInput = '';
	}

	isEmpty() {
		return isEmpty(this.value);
	}

	getSelectedOptions(): Option<T>[] {
		const allOptions = this.getAllOptions(true);
		return this.value.map((v) => allOptions.find((o) => o.value === v)!).filter(Boolean);
	}

	setSelectedOptions(options: Option<T>[]): void {
		const value = options.map((o) => o.value);
		this.value = value;
		this.valueChange.emit(value);
		this.changeDetector.markForCheck();
	}

	getSummary(): string {
		if (this.summaryAll && this.value.length === this.allOptions.length) return this.summaryAll;
		return this.allOptions
			.filter((o) => this.value.includes(o.value))
			.map((o) => o.text)
			.join(', ');
	}

	closeDropDown(event: Event) {
		this.showDropdown.set(false);
		event.preventDefault();
		event.stopPropagation();
	}

	getAllOptions(ignoreSearch?: boolean) {
		if (ignoreSearch) return this.allOptions;
		return this.allOptions.filter((option) => this.matchesSearch(option, this.searchInput));
	}
}
